import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { LoadingButton } from '@mui/lab';
import { Box, Button, Typography } from '@mui/material';

import { SAGE_FAILURE } from '../../../../../../../datahub/sage/sageApi';
import selectUser from '../../../../../../store/selectors/appSelector';
import { BLACK } from '../../../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../../../theme/fontConstants';
import {
  everifyMessage,
  everifyVerifierLabel,
  failureMessage,
} from '../../../../../../utils/sageMessages';
import {
  epochToDateInReadableFormat,
  epochToTimeInReadableFormat,
} from '../../../../../../utils/timeConverter';

import useSageWorkerControls from './useSageWorkerControls';

const styles = {
  note: {
    fontSize: '12px',
    fontFamily: PRIMARY_FONT[400],
    color: BLACK[60],
    ml: 4,
  },
  failure: {
    fontSize: '12px',
    fontFamily: PRIMARY_FONT[400],
    color: 'error.main',
    ml: 4,
  },
  action: {
    ml: 4,
    mt: 1,
  },
};

const SageWorkerControls = ({ selectedUserId, sageId, sageEVerify }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const sessionUser = useSelector(selectUser);

  const {
    everifyFailure,
    everifyResult,
    hasSageId,
    isOnboarding,
    isRunningEverify,
    onboard,
    onboardFailure,
    runEverify,
    sageId: currentSageId,
  } = useSageWorkerControls({
    selectedUserId,
    sageId,
    sageEVerify,
    dispatch,
    token: sessionUser?.token,
    refreshToken: sessionUser?.refreshToken,
  });

  const renderFailure = (failure, onRetry) =>
    failure && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={styles.failure}>{failureMessage(intl, failure)}</Typography>
        {failure.kind === SAGE_FAILURE.UNAVAILABLE && (
          <Button size="small" onClick={onRetry} sx={{ textTransform: 'none' }}>
            {intl.formatMessage({ id: 'sage.retry' })}
          </Button>
        )}
      </Box>
    );

  const verifier = everifyVerifierLabel(intl, everifyResult?.eVerifier);
  const verifiedOn = everifyResult?.verifiedOn
    ? `${epochToDateInReadableFormat(everifyResult.verifiedOn)} at ${epochToTimeInReadableFormat(
        everifyResult.verifiedOn
      )}`
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={styles.action}>
        <LoadingButton
          variant="outlined"
          size="small"
          loading={isOnboarding}
          disabled={hasSageId}
          onClick={onboard}
          sx={{ textTransform: 'none' }}
        >
          {intl.formatMessage({ id: 'sage.onboard.button' })}
        </LoadingButton>
      </Box>
      {hasSageId && (
        <Typography sx={styles.note}>
          {currentSageId
            ? intl.formatMessage({ id: 'sage.onboard.sageId' }, { sageId: currentSageId })
            : intl.formatMessage({ id: 'sage.onboard.done' })}
        </Typography>
      )}
      {renderFailure(onboardFailure, onboard)}

      <Box sx={styles.action}>
        <LoadingButton
          variant="outlined"
          size="small"
          loading={isRunningEverify}
          disabled={!hasSageId}
          onClick={runEverify}
          sx={{ textTransform: 'none' }}
        >
          {intl.formatMessage({ id: 'sage.everify.button' })}
        </LoadingButton>
      </Box>
      {!hasSageId && (
        <Typography sx={styles.note}>
          {intl.formatMessage({ id: 'sage.everify.needsOnboard' })}
        </Typography>
      )}
      {everifyResult?.status && (
        <Typography sx={styles.note}>{everifyMessage(intl, everifyResult)}</Typography>
      )}
      {verifier && (
        <Typography sx={styles.note}>
          {intl.formatMessage({ id: 'sage.everify.verifiedBy' }, { verifier })}
        </Typography>
      )}
      {verifiedOn && (
        <Typography sx={styles.note}>
          {intl.formatMessage({ id: 'sage.everify.verifiedOn' }, { verifiedOn })}
        </Typography>
      )}
      {/* A missing sage id already reads as the standing note above. */}
      {renderFailure(
        everifyFailure?.kind === SAGE_FAILURE.MISSING_SAGE_ID ? null : everifyFailure,
        runEverify
      )}
    </Box>
  );
};

SageWorkerControls.propTypes = {
  selectedUserId: PropTypes.string,
  sageId: PropTypes.string,
  sageEVerify: PropTypes.shape({
    status: PropTypes.string,
    caseNumber: PropTypes.string,
    eVerifier: PropTypes.string,
    verifiedOn: PropTypes.number,
  }),
};

export default SageWorkerControls;
